/**
 * Comprehensive logging system for the application.
 *
 * Works in both browser (Vite) and Node.js environments. Log level and
 * dev/prod gating are derived once at module load. In development, messages
 * are additionally echoed to the browser console with level-based styling.
 */

// Log levels (lower value == higher priority / always emitted first)
const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3,
  TRACE: 4,
} as const;

type LogLevel = keyof typeof LOG_LEVELS;
type LogLevelValue = (typeof LOG_LEVELS)[LogLevel];

/** Lowercase log-level name as accepted by the public logging methods. */
type LogLevelName = 'error' | 'warn' | 'info' | 'debug' | 'trace';

/**
 * Arbitrary structured context attached to a log line. Values may be anything
 * JSON-serialisable (or close to it); `unknown` keeps callers honest without
 * forcing a cast at every call site.
 */
interface LogContext {
  [key: string]: unknown;
  module?: string;
  type?: string;
}

interface PerformanceContext extends LogContext {
  error?: string;
}

// Get environment variables safely (works in both browser and Node.js)
const getEnvVar = (key: string, defaultValue: string = ''): string => {
  // Check if we're in a Node.js environment
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key] || defaultValue;
  }
  // In the browser, fall back to the provided default
  return defaultValue;
};

// Current log level (can be set via environment variable)
const currentLogLevel: LogLevelValue = (() => {
  const envLevel = getEnvVar('VITE_LOG_LEVEL', 'info');
  const levelMap: Record<string, LogLevelValue> = {
    error: LOG_LEVELS.ERROR,
    warn: LOG_LEVELS.WARN,
    info: LOG_LEVELS.INFO,
    debug: LOG_LEVELS.DEBUG,
    trace: LOG_LEVELS.TRACE,
  };
  return levelMap[envLevel.toLowerCase()] ?? LOG_LEVELS.INFO;
})();

// Check if we're in development mode
const isDevelopment: boolean = (() => {
  if (typeof process !== 'undefined' && process.env) {
    return process.env.NODE_ENV === 'development';
  }
  // In the browser, assume development by default
  return true;
})();

/**
 * Format a log message with an ISO timestamp and serialised context.
 */
function formatMessage(level: string, message: string, context: LogContext = {}): string {
  const timestamp = new Date().toISOString();
  const contextStr = Object.keys(context).length > 0 ? ` [${JSON.stringify(context)}]` : '';
  return `[${timestamp}] ${level.toUpperCase()}: ${message}${contextStr}`;
}

/**
 * Check whether logging is enabled for the given level.
 */
function shouldLog(level: string): boolean {
  const value = LOG_LEVELS[level.toUpperCase() as LogLevel];
  // Unknown levels are treated as always-on (matches prior `<=` against an
  // `undefined` coercion only for known levels; guard keeps it explicit).
  if (value === undefined) return true;
  return value <= currentLogLevel;
}

// Browser console colours per level (development only)
const LEVEL_COLORS: Record<string, string> = {
  error: '#ff4444',
  warn: '#ffaa00',
  info: '#4444ff',
  debug: '#44aa44',
  trace: '#888888',
};

/**
 * Base logging function. Routes to the appropriate console method and, in
 * development, additionally emits a styled browser-console line.
 */
function log(level: string, message: string, context: LogContext = {}): void {
  if (!shouldLog(level)) return;

  const formattedMessage = formatMessage(level, message, context);

  // Use the appropriate console method
  switch (level.toLowerCase()) {
    case 'error':
      console.error(formattedMessage);
      break;
    case 'warn':
      console.warn(formattedMessage);
      break;
    case 'info':
      console.info(formattedMessage);
      break;
    case 'debug':
      console.debug(formattedMessage);
      break;
    case 'trace':
      console.trace(formattedMessage);
      break;
    default:
      console.log(formattedMessage);
  }

  // In development, also log to the browser console with styling
  if (isDevelopment && typeof window !== 'undefined') {
    console.log(
      `%c${level.toUpperCase()}`,
      `color: ${LEVEL_COLORS[level.toLowerCase()] || '#000000'}; font-weight: bold;`,
      message,
      context
    );
  }
}

/**
 * Logger scoped to a module name. The module is attached to the context of
 * every line it emits.
 */
class Logger {
  private module: string;

  constructor(module: string = 'App') {
    this.module = module;
  }

  error(message: string, context: LogContext = {}): void {
    log('error', message, { ...context, module: this.module });
  }

  warn(message: string, context: LogContext = {}): void {
    log('warn', message, { ...context, module: this.module });
  }

  info(message: string, context: LogContext = {}): void {
    log('info', message, { ...context, module: this.module });
  }

  debug(message: string, context: LogContext = {}): void {
    log('debug', message, { ...context, module: this.module });
  }

  trace(message: string, context: LogContext = {}): void {
    log('trace', message, { ...context, module: this.module });
  }

  // Specialized logging methods

  api(method: string, url: string, status: number, duration: number, context: LogContext = {}): void {
    const level: LogLevelName = status >= 400 ? 'error' : status >= 300 ? 'warn' : 'info';
    log(level, `API ${method} ${url} - ${status} (${duration}ms)`, {
      ...context,
      module: this.module,
      type: 'api',
    });
  }

  auth(action: string, success: boolean, context: LogContext = {}): void {
    const level: LogLevelName = success ? 'info' : 'warn';
    log(level, `Auth ${action} - ${success ? 'SUCCESS' : 'FAILED'}`, {
      ...context,
      module: this.module,
      type: 'auth',
    });
  }

  camera(action: string, success: boolean, context: LogContext = {}): void {
    const level: LogLevelName = success ? 'info' : 'error';
    log(level, `Camera ${action} - ${success ? 'SUCCESS' : 'FAILED'}`, {
      ...context,
      module: this.module,
      type: 'camera',
    });
  }

  performance(operation: string, duration: number, context: LogContext = {}): void {
    const level: LogLevelName = duration > 1000 ? 'warn' : 'debug';
    log(level, `Performance ${operation} - ${duration}ms`, {
      ...context,
      module: this.module,
      type: 'performance',
    });
  }

  user(action: string, userId: string | null, context: LogContext = {}): void {
    log('info', `User ${action}`, {
      ...context,
      module: this.module,
      type: 'user',
      userId: userId ? '***' : 'anonymous',
    });
  }

  security(event: string, context: LogContext = {}): void {
    log('warn', `Security ${event}`, {
      ...context,
      module: this.module,
      type: 'security',
    });
  }

  admin(action: string, context: LogContext = {}): void {
    log('info', `Admin ${action}`, {
      ...context,
      module: this.module,
      type: 'admin',
    });
  }
}

/**
 * Create a logger instance for a specific module.
 */
function createLogger(module: string): Logger {
  return new Logger(module);
}

/**
 * Global logger instance.
 */
const logger = new Logger();

/**
 * Extract a human-readable message from an unknown thrown value.
 */
function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return String(error);
}

/**
 * Performance monitoring built on `performance.now()`.
 */
class PerformanceLogger {
  private marks: Map<string, number>;

  constructor() {
    this.marks = new Map();
  }

  start(label: string): void {
    this.marks.set(label, performance.now());
  }

  end(label: string, context: PerformanceContext = {}): number {
    const startTime = this.marks.get(label);
    if (startTime !== undefined) {
      const duration = performance.now() - startTime;
      logger.performance(label, Math.round(duration), context);
      this.marks.delete(label);
      return duration;
    }
    return 0;
  }

  measure<T>(label: string, fn: () => T, context: PerformanceContext = {}): T {
    this.start(label);
    try {
      const result = fn();
      this.end(label, context);
      return result;
    } catch (error: unknown) {
      this.end(label, { ...context, error: errorMessage(error) });
      throw error;
    }
  }

  async measureAsync<T>(
    label: string,
    fn: () => Promise<T>,
    context: PerformanceContext = {}
  ): Promise<T> {
    this.start(label);
    try {
      const result = await fn();
      this.end(label, context);
      return result;
    } catch (error: unknown) {
      this.end(label, { ...context, error: errorMessage(error) });
      throw error;
    }
  }
}

const performanceLogger = new PerformanceLogger();

// Export everything
export {
  Logger,
  createLogger,
  logger,
  performanceLogger,
  LOG_LEVELS,
  currentLogLevel,
};

// Default export
export default logger;
